/**
 * Maps each `PrintType` to a renderer that produces a PNG `data:` URL. The map is
 * keyed by the enum, so adding a `PrintType` without a renderer fails to compile.
 *
 * Renderers are pure (payload → image); they never touch the printer. The kitchen
 * ticket reads its default size from `PreferenceStore` here so the renderer itself
 * stays free of store dependencies.
 */
import { usePreferenceStore } from 'src/store/PreferenceStore';

import { summaryCanvas } from './renderers/summary.renderer';
import { PrintType, type PrintPayloads } from './print-types';
import { testReceiptCanvas } from './renderers/test.renderer';
import { itemLabelCanvas } from './renderers/item-label.renderer';
import { saleReceiptCanvas } from './renderers/sale-receipt.renderer';
import { orderReceiptCanvas } from './renderers/order-receipt.renderer';
import { inventoryReceiptCanvas } from './renderers/inventory.renderer';
import { onlinePaymentReceiptCanvas } from './renderers/online-payment.renderer';

type RendererMap = {
  [K in PrintType]: (payload: PrintPayloads[K]) => Promise<string> | string;
};

export const RENDERERS: RendererMap = {
  [PrintType.SALE_RECEIPT]: (p) =>
    saleReceiptCanvas(p.checkout, p.business, { orderDetails: p.orderDetails }),
  [PrintType.KITCHEN_TICKET]: (p) =>
    orderReceiptCanvas(p.order, {
      variant: p.variant ?? usePreferenceStore.getState().preferences.receipts.orderReceiptSize,
    }),
  [PrintType.DAILY_SUMMARY]: (p) =>
    summaryCanvas({
      summary: p.summary,
      business: p.business,
      reportPeriod: { type: 'day', date: p.reportDate },
    }),
  [PrintType.PERIOD_SUMMARY]: (p) =>
    summaryCanvas({
      summary: p.summary,
      business: p.business,
      reportPeriod: { type: 'month', monthLabel: p.monthLabel, dateRange: p.dateRange },
    }),
  [PrintType.INVENTORY_RECEIPT]: (p) => inventoryReceiptCanvas(p),
  [PrintType.ONLINE_PAYMENT_RECEIPT]: (p) => onlinePaymentReceiptCanvas(p.data),
  [PrintType.ITEM_LABEL]: (p) => itemLabelCanvas(p),
  [PrintType.TEST]: (p) => testReceiptCanvas(p.business),
};

/** Render a payload to a base64 PNG (without the `data:` URL prefix). */
export async function renderToBase64<T extends PrintType>(
  type: T,
  payload: PrintPayloads[T]
): Promise<string> {
  const renderer = RENDERERS[type] as (p: PrintPayloads[T]) => Promise<string> | string;
  const dataUrl = await renderer(payload);
  return dataUrl.replace(/^data:image\/[a-z]+;base64,/, '');
}
